import React, { useEffect } from "react";
import { Direction } from "../Models";
import { useDispatch } from "react-redux";
import { searchJourney, setDepartureDate, setDestination, setOrigin, setReturnDate } from "../Search/Actions";
import { Alert, Button, Col, Form } from "react-bootstrap";
import dayjs from "dayjs";
import { ICustomerDetails, useStateSelector } from "./Model";
import { useForm } from "react-hook-form";
import { isValidEmail, isValidTelefonnummer, validationCssClass } from "../../Shared/Validation";
import { setCustomerDetails, clearCustomerDetails } from "./Actions";
import { isTest } from "../S3BookingApp";

export const SearchForm = () => {

	const dispatch = useDispatch();

	const state = useStateSelector(state => state.booking);

	const fromStops = state.allowedFrom?.length > 1 ? state.stops?.filter(x => state.allowedFrom.includes(x.code)) : state.stops;
	const toStops = state.allowedTo?.length > 1 ? state.stops?.filter(x => state.allowedTo.includes(x.code)) : state.stops;

	const isValid = state.origin && state.destination && state.departureDate && state.returnDate ? true : false;

	const today = dayjs().startOf("day");
	const maxDate = today.add(1, "year"); //Egentligen behövs inget maxdatum alls, men 1 år är nog tillräckligt

	useEffect(() => {
		if (!state.stops)
			return;

		const from = state.defaultFrom ? state.stops.find(x => x.code === state.defaultFrom) : null;
		const to = state.defaultTo ? state.stops.find(x => x.code === state.defaultTo) : null;
		if (from)
			dispatch(setOrigin(from));
		if (to)
			dispatch(setDestination(to));
	}, [state.stops])

	const originChanged = (code: string) => {
		const stop = state.stops?.find(x => x.code === code);
		if (stop)
			dispatch(setOrigin(stop));
	};

	const destinationChanged = (code: string) => {
		const stop = state.stops?.find(x => x.code === code);
		if (stop)
			dispatch(setDestination(stop));
	};

	const dateChanged = (value: string, direction: Direction) => {
		console.debug("dateChanged", value);
		if (!value)
			return;

		const date = dayjs(value);
		if (!date.isValid() || date.isBefore(today) || date.isAfter(maxDate))
			return;

		if (direction === "outbound")
			dispatch(setDepartureDate(value));
		else if (direction === "inbound")
			dispatch(setReturnDate(value));
	};

	const searchClicked = () => {
		if (!isValid)
			return;

		const data = {
			origin: state.origin,
			destination: state.destination,
			departureDate: state.departureDate,
			returnDate: state.returnDate,
			passengers: [{ type: { code: "AD" }}],
			isTwoWay: true
		};
		dispatch(searchJourney(data));
	};


	return <Form className="search-form content">
		<Form.Row>
			<Form.Group as={Col} xs={12} sm={6} controlId="origin">
				<Form.Label>From</Form.Label>
				<Form.Control as="select" defaultValue="0" value={state.origin?.code} custom onChange={e => originChanged(e.currentTarget.value)}>
					<option value="">-- Select origin</option>
					{ fromStops.map(stop => <option key={stop.code} value={stop.code}>{stop.name}</option>)}
				</Form.Control>
			</Form.Group>
			<Form.Group as={Col} xs={12} sm={6} controlId="destination">
				<Form.Label>To</Form.Label>
				<Form.Control as="select" defaultValue="0" value={state.destination?.code} custom onChange={e => destinationChanged(e.currentTarget.value)}>
					<option value="">-- Select destination</option>
					{ toStops.map(stop => <option key={stop.code} value={stop.code}>{stop.name}</option>)}
				</Form.Control>
			</Form.Group>
		</Form.Row>
		<Form.Row>
			<Form.Group as={Col} xs={12} sm={6} controlId="departuredate">
				<Form.Label>Departure date</Form.Label>
					<Form.Control type="date" placeholder="Select departure date" min={today.format("YYYY-MM-DD")} max={maxDate.format("yyyy-MM-dd")}
					 	value={state.departureDate || ""}	required onChange={e => dateChanged(e.currentTarget.value, "outbound")} />
			</Form.Group>
			<Form.Group as={Col} xs={12} sm={6} controlId="returndate">
				<Form.Label>Return date</Form.Label>
					<Form.Control type="date" placeholder="Select return date" min={today.format("YYYY-MM-DD")} max={maxDate.format("yyyy-MM-dd")}
						value={state.returnDate || ""} required onChange={e => dateChanged(e.currentTarget.value, "inbound")} />
			</Form.Group>
		</Form.Row>
		<Button variant="primary" disabled={!isValid} onClick={searchClicked}>Search</Button>

	</Form>
};

export const CustomerDetails = (props: { show: boolean }) => {
	const { register, watch, formState: { errors, touchedFields, isDirty, isValid } } = useForm<ICustomerDetails>({ mode: 'onTouched' });

	const state = useStateSelector(state => state.booking);

	const customerdetails = watch();
	const dispatch = useDispatch();

	useEffect(() => {
		console.debug("HEJ!");
		if (!isDirty)
			return;

		if (isValid)
			dispatch(setCustomerDetails(customerdetails));
		else if (state.customerDetails)
			dispatch(clearCustomerDetails());
	}, [customerdetails.email, customerdetails.firstname, customerdetails.lastname, customerdetails.phone, customerdetails.bookingNumber, isValid]);

	if (!props.show)
		return null;


	return <>
	<hr />
	<h3>Contact information</h3>
	<Alert variant="info">You have to fill in your contact details before you can make any table bookings.</Alert>
	<Form>
		<Form.Row>
			<Form.Group as={Col} xs={12} sm={6} controlId="firstname">
				<Form.Label>First name</Form.Label>
				<Form.Control type="text" placeholder="Firstname" {...register("firstname", { required: true }) }
					className={validationCssClass({ errors, touchedFields }, "firstname")} />
			</Form.Group>
			<Form.Group as={Col} xs={12} sm={6} controlId="lastname">
				<Form.Label>Last name</Form.Label>
				<Form.Control type="text" placeholder="Lastname" {...register("lastname", { required: true }) }
					className={validationCssClass({ errors, touchedFields }, "lastname")} />
			</Form.Group>
		</Form.Row>
		<Form.Row>
			<Form.Group as={Col} xs={12} sm={6} controlId="email">
				<Form.Label>Email address</Form.Label>
				<Form.Control type="email" autoComplete="email" placeholder="firstname.lastname@example.com" {...register("email",  { validate: x => isValidEmail(x, false) }) }
					className={validationCssClass({ errors, touchedFields }, "email")}  />
			</Form.Group>
			<Form.Group as={Col} xs={12} sm={6} controlId="phone">
				<Form.Label>Mobile phone</Form.Label>
				<Form.Control type="tel" autoComplete="tel" placeholder="+46701234567" {...register("phone", { validate: x => isValidTelefonnummer(x, false) }) }
					className={validationCssClass({ errors, touchedFields }, "phone")} />
			</Form.Group>
		</Form.Row>
		<Form.Row>
			<Form.Group as={Col} xs={12} sm={6} controlId="bookingNumber">
				<Form.Label>Booking number (8 digits)</Form.Label>
				<Form.Control type="text" placeholder="12345678" {...register("bookingNumber", { required: true, pattern: /^\d{8}$/ }) }
					className={validationCssClass({ errors, touchedFields }, "bookingNumber")} />
			</Form.Group>
		</Form.Row>
	</Form>

	{ isTest() && <code>{JSON.stringify(state.customerDetails)}</code> }
	</>
};
